import { useProductsTFunction } from '@retail/products/i18n';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { ExternalLinkIcon } from '@shared/custom-icons';
import { EquivalentItemTooltip } from '../MgItem/EquivalentItemTooltip';
import { formatPrice } from '@retail/products/utils';
import { useTranslation } from 'react-i18next';
import { TrendingDown, TrendingUp } from '@mui/icons-material';
import dayjs from 'dayjs';
import { CompetitorPriceCell } from './CompetitorPriceCell';

interface CompPrice {
  unit: string;
  price: number;
  priceDate: string;
}

interface Props {
  setItemToView?: (mgItemNumber: number) => void;
  currentPrice: CompPrice;
  prevPrice?: CompPrice;
  url?: string;
  equivalentItem?: number;
  ownPrice?: number;
  ownPriceUnit?: string;
}

export function PriceCompareCell({
  currentPrice,
  prevPrice,
  setItemToView,
  url,
  equivalentItem,
  ownPrice,
  ownPriceUnit,
}: Props) {
  const { i18n } = useTranslation();
  const t = useProductsTFunction();

  return (
    <Stack alignItems="center">
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="subtitle2">
          {t('products.prices.pricePerUnit', {
            price: formatPrice(currentPrice.price, i18n.language),
            unit: currentPrice.unit,
          })}
        </Typography>
        {url && (
          <Box component="a" href={url} target="_blank" display="flex">
            <ExternalLinkIcon
              fontSize="inherit"
              titleAccess={t('products.productList.competitorPrice.openInNew')}
            />
          </Box>
        )}
        {equivalentItem && (
          <EquivalentItemTooltip
            onClick={() => setItemToView && setItemToView(equivalentItem)}
            mgItemNumber={equivalentItem}
          />
        )}
        <Tooltip
          title={
            prevPrice && (
              <Stack alignItems="center">
                <Typography>{t('products.prices.competitors.historic.lastPrice')}</Typography>
                <CompetitorPriceCell
                  setItemToView={setItemToView}
                  currentPrice={prevPrice}
                  url={url}
                  eqMgItemNumberSrc={equivalentItem}
                />
              </Stack>
            )
          }
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {prevPrice &&
              (prevPrice.price < currentPrice.price ? (
                <TrendingUp color="success" fontSize="small" />
              ) : (
                <TrendingDown color="error" fontSize="small" />
              ))}
          </Stack>
        </Tooltip>
      </Stack>

      <Typography variant="body3">
        {currentPrice.priceDate ? dayjs(currentPrice.priceDate).format('ll') : '-'}
      </Typography>

      <Stack flexDirection="row" alignItems="center" gap={0.5}>
        <Typography variant="body3">{t('products.prices.own.price')}:</Typography>
        <Typography variant="body3" color="textPrimary">
          {ownPrice
            ? `${t('products.prices.pricePerUnit', {
                price: formatPrice(ownPrice, i18n.language),
                unit: ownPriceUnit ?? '-',
              })}`
            : '-'}
        </Typography>
      </Stack>
    </Stack>
  );
}
